<template>
  <vx-card>
    <div class="header-block">
      <h4>Histórico de consentimento</h4>
      <p>
        É o que se apresenta numa fiscalização: quem aceitou o quê, em qual versão do
        documento e quando. A tabela é <strong>append-only</strong> — revogar cria uma
        linha nova, nada é reescrito (LGPD, Art. 8º, §2º).
      </p>
    </div>

    <div class="filters-row">
      <vs-input
        v-model="filters.email"
        label="E-mail do titular"
        placeholder="Buscar por e-mail"
        class="filter-field"
        @keyup.enter="load"
      />

      <vs-select v-model="filters.purpose" label="Finalidade" class="filter-field" @input="load">
        <vs-select-item :value="''" text="Todas" />
        <vs-select-item v-for="(label, key) in purposeLabels" :key="key" :value="key" :text="label" />
      </vs-select>

      <vs-button size="small" @click="load">Filtrar</vs-button>
    </div>

    <div v-if="loading" class="state-message">Carregando…</div>

    <div v-else-if="!rows.length" class="state-message">Nenhum registro com estes filtros.</div>

    <table v-else class="privacy-table">
      <thead>
        <tr>
          <th>Titular</th>
          <th>Finalidade</th>
          <th>Situação</th>
          <th>Versão</th>
          <th>Origem</th>
          <th>Quando</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.id">
          <td>
            <div class="subject">
              <strong>{{ row.user ? row.user.name : 'Visitante não identificado' }}</strong>
              <span>{{ row.user ? row.user.email : row.visitor_id }}</span>
            </div>
          </td>
          <td>{{ purposeLabels[row.purpose] || row.purpose }}</td>
          <td>
            <span class="pill" :class="row.granted ? 'granted' : 'revoked'">
              {{ row.granted ? 'Concedido' : 'Revogado' }}
            </span>
          </td>
          <td>{{ row.version || '—' }}</td>
          <td class="source-cell">{{ row.source || '—' }}</td>
          <td>{{ formatDateTime(row.created_at) }}</td>
        </tr>
      </tbody>
    </table>

    <div v-if="pagination.last_page > 1" class="pagination-row">
      <vs-button size="small" type="border" :disabled="page <= 1" @click="go(page - 1)">Anterior</vs-button>
      <span>Página {{ pagination.current_page }} de {{ pagination.last_page }}</span>
      <vs-button size="small" type="border" :disabled="page >= pagination.last_page" @click="go(page + 1)">Próxima</vs-button>
    </div>
  </vx-card>
</template>

<script>
import PrivacyService from '@/services/api/PrivacyService'

export default {
  name: 'PrivacyConsentsIndex',
  data() {
    return {
      loading: true,
      rows: [],
      page: 1,
      pagination: { current_page: 1, last_page: 1 },
      filters: { email: '', purpose: '' },
      purposeLabels: {
        terms: 'Termos de Uso',
        privacy: 'Política de Privacidade',
        cookies_essential: 'Cookies essenciais',
        cookies_performance: 'Cookies de desempenho',
        cookies_functional: 'Cookies de funcionalidade',
        cookies_marketing: 'Cookies de marketing e indicação',
        health_data: 'Dado de saúde (cancelamento médico)',
        guardian: 'Responsável legal',
        communications: 'Comunicações não transacionais'
      }
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const response = await PrivacyService.build(this.$vs).consentsGrid({
          email: this.filters.email,
          purpose: this.filters.purpose,
          page: this.page
        })

        this.rows = response.data || []
        this.pagination = response
      } catch (e) {
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    go(page) {
      this.page = page
      this.load()
    },
    formatDateTime(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('pt-BR')
    }
  }
}
</script>

<style lang="scss" scoped>
.header-block {
  margin-bottom: 1.25rem;

  h4 { margin: 0 0 0.35rem; }

  p {
    margin: 0;
    font-size: 0.82rem;
    color: #6e6b7b;
    line-height: 1.5;
    max-width: 720px;
  }
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.filter-field {
  min-width: 220px;
}

.state-message {
  padding: 2rem 0;
  text-align: center;
  color: #6e6b7b;
}

.privacy-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th {
    text-align: left;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #6e6b7b;
    padding: 0.5rem;
    border-bottom: 1px solid #ebe9f1;
  }

  td {
    padding: 0.65rem 0.5rem;
    border-bottom: 1px solid #f5f4f7;
  }
}

.subject {
  display: flex;
  flex-direction: column;

  span {
    font-size: 0.72rem;
    color: #6e6b7b;
    word-break: break-all;
  }
}

.source-cell {
  font-family: monospace;
  font-size: 0.78rem;
  color: #6e6b7b;
}

.pill {
  display: inline-block;
  padding: 0.15rem 0.65rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;

  &.granted { background: #e8f5e9; color: #2e7d32; }
  &.revoked { background: #eceff1; color: #546e7a; }
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.25rem;
  font-size: 0.82rem;
  color: #6e6b7b;
}
</style>
